import { Button } from "@/components/ui/button";
import { CheckCircle, Clock, AlertTriangle, FileText, RefreshCw, Download, Send, ArrowRight, CreditCard, Trash2 } from "lucide-react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  pending: { label: "Pending", icon: Clock, className: "status-starting-soon" },
  paid: { label: "Paid", icon: CheckCircle, className: "status-completed" },
  overdue: { label: "Overdue", icon: AlertTriangle, className: "bg-red-100 text-red-800" },
};

export function PaymentFilters({ filterStatus, setFilterStatus }) {
  return (
    <div className="flex gap-2">
      {["all", "pending", "paid", "overdue"].map((s) => (
        <Button
          key={s}
          data-testid={`filter-${s}`}
          variant={filterStatus === s ? "default" : "outline"}
          size="sm"
          onClick={() => setFilterStatus(s)}
          className={filterStatus === s ? "bg-[#5C3A2E] text-white rounded-full" : "border-[#EADBC8] text-[#5C3A2E] rounded-full hover:bg-[#EADBC8]/30"}
        >
          {s === "all" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
        </Button>
      ))}
    </div>
  );
}

export function PaymentCard({ payment, getStudentName, getStudentEmail, onMarkPaid, onGenerateInvoice, onSendReminder, onCreateNextCycle, onEdit, onDelete }) {
  const sc = statusConfig[payment.status] || statusConfig.pending;
  const StatusIcon = sc.icon;

  return (
    <div data-testid={`payment-card-${payment.id}`} className="bg-white rounded-xl shadow-[0_8px_30px_rgb(92,58,46,0.08)] border border-[#EADBC8]/30 p-5 sm:p-6 studio-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="font-heading text-lg text-[#5C3A2E] font-semibold">{getStudentName(payment.student_id)}</h3>
            <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider ${sc.className}`}>
              <StatusIcon className="w-3 h-3" /> {sc.label}
            </span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#2B2B2B]/50">
            <span>{getStudentEmail(payment.student_id)}</span>
            <span>Due: {payment.due_date || "Not set"}</span>
            {payment.invoice_number && <span>Invoice #INV-{String(payment.invoice_number).padStart(3, "0")}</span>}
          </div>
          {payment.notes && <p className="text-xs text-[#2B2B2B]/40 mt-1">{payment.notes}</p>}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-2xl font-semibold text-[#5C3A2E] font-body">&euro;{Number(payment.amount).toFixed(2)}</span>
          <div className="flex items-center gap-1">
            {payment.status !== "paid" && (
              <Button data-testid={`mark-paid-${payment.id}`} variant="ghost" size="sm" onClick={() => onMarkPaid(payment.id)} title="Mark as Paid" className="text-green-600 hover:bg-green-50 rounded-full h-8 w-8 p-0">
                <CheckCircle className="w-4 h-4" />
              </Button>
            )}
            <Button data-testid={`gen-invoice-${payment.id}`} variant="ghost" size="sm" onClick={() => onGenerateInvoice(payment.id)} title={payment.invoice_path ? "Regenerate Invoice" : "Generate Invoice"} className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0">
              {payment.invoice_path ? <RefreshCw className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
            </Button>
            {payment.invoice_path && (
              <a href={`${API_BASE}/api/payments/${payment.id}/invoice`} target="_blank" rel="noopener noreferrer">
                <Button data-testid={`download-invoice-${payment.id}`} variant="ghost" size="sm" title="Download Invoice" className="text-[#C6A969] hover:bg-[#C6A969]/10 rounded-full h-8 w-8 p-0">
                  <Download className="w-4 h-4" />
                </Button>
              </a>
            )}
            {payment.status !== "paid" && (
              <Button data-testid={`send-reminder-${payment.id}`} variant="ghost" size="sm" onClick={() => onSendReminder(payment.id)} title="Send Reminder" className="text-[#C6A969] hover:bg-[#C6A969]/10 rounded-full h-8 w-8 p-0">
                <Send className="w-4 h-4" />
              </Button>
            )}
            {payment.status === "paid" && (
              <Button data-testid={`next-cycle-${payment.id}`} variant="ghost" size="sm" onClick={() => onCreateNextCycle(payment.id)} title="Create Next Cycle" className="text-[#C6A969] hover:bg-[#C6A969]/10 rounded-full h-8 w-8 p-0">
                <ArrowRight className="w-4 h-4" />
              </Button>
            )}
            <Button data-testid={`edit-payment-${payment.id}`} variant="ghost" size="sm" onClick={() => onEdit(payment)} title="Edit" className="text-[#5C3A2E] hover:bg-[#EADBC8]/30 rounded-full h-8 w-8 p-0">
              <CreditCard className="w-4 h-4" />
            </Button>
            {onDelete && (
              <Button data-testid={`delete-payment-${payment.id}`} variant="ghost" size="sm" onClick={() => onDelete(payment)} title="Delete" className="text-red-400 hover:bg-red-50 rounded-full h-8 w-8 p-0">
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
